import * as Dialog from '@radix-ui/react-dialog';
import {
  DayPicker,
  type ChevronProps,
  type ClassNames,
  type DateRange,
  type OnSelectHandler,
} from 'react-day-picker';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import { cn } from '../../utils/className';
import { BottomSheet } from '../BottomSheet';
import { MARKED_DAY_CLASS_NAME, type PresetOption } from './TimeRangeSelector';

function SheetChevron({ orientation }: ChevronProps) {
  const Icon = orientation === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return <Icon className='h-4 w-4 [&_path]:fill-current' />;
}

function formatWeekdayName(weekday: Date): string {
  return weekday.toLocaleDateString('en-GB', { weekday: 'narrow' });
}

// Doubled selectors beat the weekend, outside and marked-day classes, which
// have the same specificity otherwise.
const SELECTED_DAY_CLASS_NAME = `[&>button]:bg-surface-focus
  [&&>button]:text-inverted [&&>button]:after:bg-inverted`;

// The modifier classes land on the cell, so they style the button inside it.
// The day button sets no colour: `text-primary` in index.css is unlayered and
// would beat every colour a modifier sets.
const CALENDAR_CLASS_NAMES: Partial<ClassNames> = {
  // Capped, so the day cells keep their shape in a wide window.
  root: 'mx-auto w-full max-w-sm',
  months: 'w-full',
  month: 'relative w-full',
  month_caption: 'flex h-8 items-center justify-center',
  caption_label: 'size-p2 text-secondary font-bold',
  button_previous: `text-secondary absolute top-0 left-0 flex h-8 w-8
    cursor-pointer items-center justify-center rounded-full`,
  button_next: `text-secondary absolute top-0 right-0 flex h-8 w-8
    cursor-pointer items-center justify-center rounded-full`,
  chevron: '',
  // The spacing keeps 4px between the weeks.
  month_grid: `mt-3 w-full table-fixed border-separate border-spacing-x-0
    border-spacing-y-1`,
  weekdays: '',
  weekday: `size-caption text-secondary h-8 text-center font-normal
    tracking-[0.08em] uppercase`,
  week: '',
  day: 'p-0 text-center',
  // Six weeks share the height the rest of the sheet leaves: 95dvh minus about
  // 546px for the title, chips, calendar header and From/Till block. A week is
  // 48px tall when it fits, and at least 32px on a short phone.
  day_button: `size-p2 flex h-[clamp(2rem,calc((95dvh-546px)/6),3rem)] w-full
    cursor-pointer items-center
    justify-center`,
  today: '',
  selected: '',
  // The start and end fill the whole cell, so the band between them joins up.
  // A day selected alone is both, and gets all four corners rounded.
  range_start: `${SELECTED_DAY_CLASS_NAME} [&>button]:rounded-l-xl
    [&>button]:font-bold`,
  range_end: `${SELECTED_DAY_CLASS_NAME} [&>button]:rounded-r-xl`,
  range_middle: '[&>button]:bg-surface-focus-support',
  // Days of the next and previous month stay selectable, so they keep their
  // colour. Only a future day looks disabled.
  outside: '',
  disabled: '[&&>button]:text-disabled [&>button]:cursor-not-allowed',
};

interface TimeFieldProps {
  label: string;
  date: string;
  time: string;
  onTimeChange: (time: string) => void;
}

function TimeField({ label, date, time, onTimeChange }: TimeFieldProps) {
  return (
    <div className='flex items-center justify-between gap-4'>
      <div className='flex min-w-0 flex-col'>
        <span className='size-p2 text-primary'>{label}</span>
        <span className='size-p1 text-primary truncate font-bold'>{date}</span>
      </div>
      <input
        type='time'
        aria-label={`${label} time`}
        value={time}
        onChange={(e) => onTimeChange(e.target.value)}
        className={cn(
          'size-p2 text-primary w-30 shrink-0 rounded-sm px-2 py-2.5',
          'border-border-primary bg-surface-primary border',
          'focus:border-border-focus focus:outline-none'
        )}
      />
    </div>
  );
}

interface TimeRangeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: { value: PresetOption; label: string }[];
  selectedOption: PresetOption;
  onOptionSelect: (option: PresetOption) => void;
  dateRange: DateRange | undefined;
  onDateSelect: OnSelectHandler<DateRange | undefined>;
  markedDays?: Date[];
  fromDate: string;
  tillDate: string;
  fromTime: string;
  toTime: string;
  onFromTimeChange: (time: string) => void;
  onToTimeChange: (time: string) => void;
  showOrderError: boolean;
  isApplyDisabled: boolean;
  onApply: () => void;
}

/** The time range picker for the phone layout, as a bottom sheet. */
export function TimeRangeSheet({
  open,
  onOpenChange,
  options,
  selectedOption,
  onOptionSelect,
  dateRange,
  onDateSelect,
  markedDays,
  fromDate,
  tillDate,
  fromTime,
  toTime,
  onFromTimeChange,
  onToTimeChange,
  showOrderError,
  isApplyDisabled,
  onApply,
}: TimeRangeSheetProps) {
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title='Select time range'
      titleTone='brand'
      className='h-[95dvh] rounded-t-lg border-0'
      scrollable={false}
    >
      <div className='flex h-full flex-col'>
        <div
          className='border-border-secondary flex shrink-0 gap-2 overflow-x-auto
            border-b px-6 py-3 [scrollbar-width:none]
            [&::-webkit-scrollbar]:hidden'
        >
          {options.map(({ value, label }) => (
            <button
              key={value}
              type='button'
              onClick={() => onOptionSelect(value)}
              className={cn(
                'size-caption shrink-0 cursor-pointer rounded-full border',
                'px-4 py-2',
                'transition-colors',
                selectedOption === value
                  ? 'text-primary border-blue-200 bg-blue-100 font-bold'
                  : 'border-border-primary bg-surface-primary text-primary'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className='p-4'>
          <DayPicker
            mode='range'
            selected={dateRange}
            onSelect={onDateSelect}
            disabled={{ after: new Date() }}
            modifiers={{
              weekend: { dayOfWeek: [0, 6] },
              marked: markedDays ?? [],
              // A start without an end yet: the second tap ends the range.
              pending_start: dateRange?.to ? [] : (dateRange?.from ?? []),
            }}
            modifiersClassNames={{
              weekend: '[&>button]:text-secondary',
              marked: MARKED_DAY_CLASS_NAME,
              pending_start: `${SELECTED_DAY_CLASS_NAME}
                [&>button]:rounded-l-xl [&>button]:font-bold`,
            }}
            defaultMonth={dateRange?.from}
            navLayout='around'
            weekStartsOn={1}
            showOutsideDays
            fixedWeeks
            formatters={{ formatWeekdayName }}
            components={{ Chevron: SheetChevron }}
            classNames={CALENDAR_CLASS_NAMES}
          />
        </div>

        {/* Pinned to the bottom of the sheet, under the thumb. */}
        <div
          className='border-border-secondary mt-auto flex shrink-0 flex-col
            gap-4 border-t px-6 pt-6 pb-10'
        >
          <div className='flex flex-col gap-3'>
            <TimeField
              label='From'
              date={fromDate}
              time={fromTime}
              onTimeChange={onFromTimeChange}
            />
            <div className='bg-border-secondary h-px' />
            <TimeField
              label='Till'
              date={tillDate}
              time={toTime}
              onTimeChange={onToTimeChange}
            />

            {showOrderError && (
              <p className='size-caption text-negative'>
                &quot;From&quot; must be before &quot;Till&quot;
              </p>
            )}
          </div>

          <div className='flex flex-col gap-3'>
            <button
              type='button'
              disabled={isApplyDisabled}
              onClick={onApply}
              className={cn(
                'size-p2 h-[42px] w-full rounded-lg px-3 font-medium',
                'bg-surface-focus text-inverted cursor-pointer',
                'disabled:bg-surface-disabled disabled:text-disabled',
                'disabled:cursor-not-allowed'
              )}
            >
              Apply filter
            </button>

            <Dialog.Close asChild>
              <button
                type='button'
                className='size-p2 text-primary h-[38px] w-full cursor-pointer
                  underline'
              >
                Cancel
              </button>
            </Dialog.Close>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}
